import React, {ReactElement} from "react";
import {TableDataSourceFragmentProps, TableDataSourceFragmentState} from "@/shared/TableDataSourceFragment";
import {PageFragment} from "@/shared/PageFragment";
import {Action, ACTION_OK, ACTION_SPACE, ActionBase, CreateActionsArgs} from "@/shared/types";
import {AddOutlined} from "@mui/icons-material";
import {TableDataContainer, TableDataRow} from "@/shared/TableDataContainer";
import {TableData} from "@/shared/tabledata";
import {
  SiteDomain,
  SiteDomains,
  SiteDomainStatus,
  SiteDomainType,
  VANTAGE_PRACTICE_SITE_DOMAIN_STATUS_KTM
} from "@/vantage-shared/types";
import {EditSiteDomainHelper} from "./EditSiteDomainHelper";
import {ListTableDataSourceFragment} from "@/shared/ListTableDataSourceFragment";
import {BaseApp} from "@/shared/BaseApp";
import {PracticeData} from "../../../libwyze-shared/types";

class SiteDomainsTableDataRow implements TableDataRow {

  @TableData({name: "Name"})
  name: string;

  @TableData({
    name: "Status",
    cellSize: "medium",
    type: "enum",
    enumValues: VANTAGE_PRACTICE_SITE_DOMAIN_STATUS_KTM.kts().values,
    enumColors: VANTAGE_PRACTICE_SITE_DOMAIN_STATUS_KTM.metadataValues("colorValue").values
  })
  status: SiteDomainStatus;

  @TableData({
    name: "Actions",
    type: "actions",
    actions: (event, args: CreateActionsArgs) => {
      const data = args.data as SiteDomain;
      return [
        data.type !== SiteDomainType.SYSTEM
          ? new Action("Delete", () => EditSiteDomainHelper.deleteSiteDomain(data))
          : null,
        new Action("Open URL", () => {
          window.open(args.row.name, "_blank");
        })
      ].filter(Boolean);
    }
  })
  actions: any;
}

export type SiteDomainsFragmentProps = TableDataSourceFragmentProps & {
  siteId: string,
}

export class SiteDomainsFragment extends ListTableDataSourceFragment<SiteDomain, SiteDomainsTableDataRow, SiteDomains, SiteDomainsFragmentProps> {

  private readonly actions: ActionBase[] = [
    ACTION_SPACE,
    new Action("Add Custom Domain", () => {
      EditSiteDomainHelper.addSiteDomain(this.props.siteId);
    }, AddOutlined)
      .setVariant("contained"),
  ];

  constructor(props: SiteDomainsFragmentProps, context: any) {
    super(props, context, new SiteDomains(PracticeData.provider().practiceId));
  }

  protected onCreateState(): TableDataSourceFragmentState {
    return {
      ...super.onCreateState(),
      title: "Manage Domains",
    };
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG | PageFragment.STYLE_BACK_BUTTON_FLAG;
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx(this.actions);
  }

  createTableDataRow(): SiteDomainsTableDataRow {
    return new SiteDomainsTableDataRow();
  }

  applyTableDataToRow(data: SiteDomain, content: SiteDomainsTableDataRow): void {
    content.status = data.status;
    content.name = data.name;
  }

  onTableDataSelected(data: SiteDomain) {
    if (data.type === SiteDomainType.SYSTEM) {
      BaseApp.CONTEXT.showTextDialog("Not editable", "System domains are automatically created and not editable in Vantage.", ACTION_OK);
      return;
    }
  }

  onTableCellChanged(data: SiteDomain, cellId: string, value: any): void {
  }

  protected async fetchOnMount(): Promise<void> {
    return super.fetchOnMount();
  }

  renderContent(): ReactElement {
    const siteId = this.props.siteId;
    const systemDomain = SiteDomain.createSystem(SiteDomain.getDefaultSiteName(PracticeData.provider().practiceId, siteId), siteId);
    systemDomain.status = SiteDomainStatus.CONNECTED;
    const data = [
      systemDomain,
      ...this.loader.getListItems()
    ];
    return <TableDataContainer
      dataTotal={data.length}
      data={data}
      selectModeConfig={this.props.selectModeConfig ? {initialSelection: this.props.selectModeConfig.initialSelection} : null}
      source={this}
      multiSelectListener={this}/>;
  }
}